// Route catalog — built from the actual Next.js /src/app directory structure.
// Route groups like (dashboard), (auth) are stripped from the URL automatically by Next.js.
// Dynamic segments [id] are shown as {param_name} for the AI.
//
// TAB NAVIGATION RULES:
//   Pages whose tab IS a URL param (integrations, leads, customers):
//     → Use "navigation" event: { "type": "navigation", "path": "/integrations", "query": { "tab": "calls" } }
//   Pages whose tab is LOCAL STATE only (command agent, sales agent, support agent, users, settings, billing, help, documents):
//     → Navigate to the page first, then use "open_tab" event: { "type": "open_tab", "tab": "settings" }
//     → OR combine: navigation to page + open_tab in same response stream
//
// API COMMENTS on detail routes describe the backend endpoints + accepted fields for that resource.

export interface RouteEntry {
  path: string;          // URL pattern — {param} = dynamic segment
  name: string;
  params?: string[];     // required path params
  tabs?: string[];       // valid tab values
  query?: Record<string, string>; // accepted query params → description
}

export const ROUTE_CATALOG: RouteEntry[] = [
  // ── Dashboard ─────────────────────────────────────────────────────────────
  { path: "/workforce",           name: "Teams Overview" },
  { path: "/insights/logs",   name: "Activity Log" },
  { path: "/usage",           name: "Usage" },
  { path: "/scheduled-job",   name: "Scheduled Jobs" },

  // ── CRM ───────────────────────────────────────────────────────────────────
  { path: "/crm",             name: "CRM / Persons List" },
  { path: "/crm/{id}",        name: "Person Detail",     params: ["id"],
    query: {
      name:        "Display name shown in breadcrumb",
      interaction: "Open a specific interaction by ID",
      type:        "Interaction type e.g. CALL_PREVIEW | WHATSAPP | EMAIL",
    }},

  // ── Users & Roles ─────────────────────────────────────────────────────────
  { path: "/settings",        name: "Users & Roles",     tabs: ["users", "roles"] },

  // ── Documents ─────────────────────────────────────────────────────────────
  { path: "/documents",       name: "Documents",         tabs: ["datasource", "documents"] },

  // ── Data Tables ───────────────────────────────────────────────────────────
  { path: "/data-tables",      name: "Data Tables" },
  { path: "/data-tables/{id}", name: "Data Table",  params: ["id"] },

  // ── Document Agent ────────────────────────────────────────────────────────
  { path: "/document-agent",      name: "Document Agents" },
  { path: "/document-agent/{id}", name: "Document Agent", params: ["id"] },

  // ── Interactions ──────────────────────────────────────────────────────────
  { path: "/interactions",    name: "Interactions" },



  // ── Integrations ──────────────────────────────────────────────────────────
  { path: "/integrations",               name: "Integrations",   tabs: ["apps", "calls", "triggers", "custom-app"],
    query: { tab: "Active tab: apps | calls | triggers | custom-app" }},
  { path: "/integrations/custom-app",    name: "Custom Apps" },
  { path: "/integrations/custom-app/{id}", name: "Custom App Detail", params: ["id"] },

  // ── Blueprints ────────────────────────────────────────────────────────────
  { path: "/blueprints",      name: "Blueprints" },

  // ── Billing ───────────────────────────────────────────────────────────────
  { path: "/billing",         name: "Billing",           tabs: ["usage", "billing", "credits"] },

  // ── Settings ──────────────────────────────────────────────────────────────
  { path: "/settings",        name: "Settings",          tabs: ["organization", "user", "security", "webhook"] },

  // ── Help ──────────────────────────────────────────────────────────────────
  { path: "/help",            name: "Help",              tabs: ["documentation", "support"] },

  // ── Sales Team ────────────────────────────────────────────────────────────
  { path: "/workforce/sales-team/leads",                    name: "Leads List",
    query: { page: "Page number", tab: "Active tab" }},
  { path: "/workforce/sales-team/leads/{id}",               name: "Lead Detail",          params: ["id"],
    query: { name: "Lead display name (breadcrumb)", interaction: "Open interaction by ID", type: "Interaction type e.g. CALL_PREVIEW" }},
  { path: "/workforce/sales-team/sales-agents",             name: "Sales Agents List" },
  { path: "/workforce/sales-team/sales-agents/{id}",        name: "Sales Agent Detail",   params: ["id"], tabs: ["settings", "tools", "knowledge-base", "channels"],
    query: { name: "Agent display name (breadcrumb)" }},
  { path: "/workforce/sales-team/sales-activity-log",       name: "Sales Activity Log" },

  // ── Support Team ──────────────────────────────────────────────────────────
  { path: "/workforce/support-team/customers",              name: "Customers List",
    query: { page: "Page number", tab: "Active tab" }},
  { path: "/workforce/support-team/customers/{id}",         name: "Customer Detail",      params: ["id"],
    query: { name: "Customer display name (breadcrumb)", interaction: "Open interaction by ID", type: "Interaction type e.g. CALL_PREVIEW" }},
  { path: "/workforce/support-team/support-agents",         name: "Support Agents List" },
  { path: "/workforce/support-team/support-agents/{id}",    name: "Support Agent Detail", params: ["id"], tabs: ["settings", "tools", "knowledge-base", "channels"],
    query: { name: "Agent display name (breadcrumb)" }},
  { path: "/workforce/support-team/support-activity-log",   name: "Support Activity Log" },

  // ── Analyst Team ──────────────────────────────────────────────────────────
  { path: "/workforce/analyst-team/analyst-agent",                    name: "Analyst Agents List" },
  { path: "/workforce/analyst-team/analyst-agent/{id}",               name: "Analyst Agent Detail", params: ["id"], tabs: ["analysis", "settings", "views"] },
  { path: "/workforce/analyst-team/analyst-agent/{id}/analysis/{analysisId}", name: "Analysis Detail", params: ["id", "analysisId"] },
  { path: "/workforce/analyst-team/analyst-view/{id}",                name: "Analyst View",         params: ["id"] },
  { path: "/workforce/analyst-team/analyst-file-manager",             name: "Analyst File Manager" },
  { path: "/workforce/analyst-team/analyst-file-manager/{id}",        name: "Analyst File Manager Detail", params: ["id"] },
  { path: "/workforce/analyst-team/analyst-activity-log",             name: "Analyst Activity Log" },

  // ── Computer Use Team ─────────────────────────────────────────────────────
  { path: "/workforce/computer-use-team/computer-use-agent",           name: "Computer Use Agents List" },
  { path: "/workforce/computer-use-team/computer-use-agent/{id}",      name: "Computer Use Agent Detail", params: ["id"], tabs: ["agents-runs", "settings", "knowledge-base"] },
  { path: "/workforce/computer-use-team/computer-use-agent/{id}/details/{run_id}", name: "Run Detail", params: ["id", "run_id"] },
  { path: "/workforce/computer-use-team/computer-use-runs",            name: "Computer Use Runs" },
  { path: "/workforce/computer-use-team/computer-use-runs/{id}",       name: "Computer Use Run Detail", params: ["id"] },
  { path: "/workforce/computer-use-team/computer-use-activity-logs",   name: "Computer Use Activity Logs" },

  // ── Command Team ──────────────────────────────────────────────────────────
  // Agents list — GET /api/command-team/agents?search=&page=&sort_by=&sort_order=
  { path: "/workforce/command-team/agents",            name: "Command Agents List",
    query: { page: "Page number (default 1)", search: "Search by agent name" }},

  // Agent detail — tabs use open_tab action (local state, NOT URL param)
  // Update:   PATCH /api/command-team/agents/{agent_id}  body: { name, description, role, persona, restrictions, llm_provider, llm_model, llm_timeout, llm_temperature, llm_max_tokens, is_public, shared_roles, app_integrations, custom_apps, datasources }
  // Activate: PATCH /api/command-team/agents/{agent_id}/activate  (no body)
  // Delete:   DELETE /api/command-team/agents/{agent_id}
  { path: "/workforce/command-team/agents/{agent_id}", name: "Command Agent Detail", params: ["agent_id"],
    tabs: ["settings", "knowledge-base", "scheduled-jobs"],
    query: { name: "Agent display name (breadcrumb)" }},

  // Chat — unfiled sessions: GET /api/command-team/agents/{agent_id}/sessions?page=&limit=&sort_by=updated_at&sort_order=desc&search=
  //         project sessions: GET .../sessions?project_id=X  OR  GET /api/command-team/projects/{id}/sessions
  // Create session: POST /api/command-team/agents/{agent_id}/sessions  body: { project_id? }
  // Update session: PATCH /api/command-team/agents/{agent_id}/sessions/{session_id}  body: { pinned?, title?, project_id? }
  // Delete session: DELETE /api/command-team/agents/{agent_id}/sessions/{session_id}
  // Messages: GET/POST .../messages, GET .../messages/stream, POST .../messages/{id}/regenerate — see src/app/api/command-team/
  { path: "/workforce/command-team/chat",              name: "Command Chat" },
  { path: "/workforce/command-team/chat/{agent_id}",   name: "Command Agent Chat", params: ["agent_id"],
    query: { session_id: "Resume a specific existing session by ID", name: "Agent display name (breadcrumb)" }},

  { path: "/workforce/command-team/prompts",           name: "Command Prompts" },
  { path: "/workforce/command-team/skills",            name: "Command Skills",
    query: { search: "Search skills by name or description" }},
  { path: "/workforce/command-team/skills/{skillId}",  name: "Command Skill Detail", params: ["skillId"] },
  { path: "/workforce/command-team/file-manager",      name: "Command File Manager",
    query: { page: "Page number" }},
  { path: "/workforce/command-team/projects",          name: "Command Projects" },
  { path: "/workforce/command-team/chats",             name: "Command Chats" },
  { path: "/workforce/command-team/scheduled",         name: "Command Scheduled" },

  // Legacy v1 (command-team-old)
  { path: "/workforce/command-team-old/activity-logs", name: "Command Activity Logs (legacy)",
    query: { page: "Page number" }},

  // ── Tasks Team ────────────────────────────────────────────────────────────
  { path: "/workforce/tasks-team/task-agents",            name: "Task Agents List" },
  { path: "/workforce/tasks-team/task-agents/{id}",       name: "Task Agent Detail",    params: ["id"] },
  { path: "/workforce/tasks-team/tasks-runs",             name: "Task Runs" },
  { path: "/workforce/tasks-team/tasks-runs/{id}",        name: "Task Run Detail",      params: ["id"] },
  { path: "/workforce/tasks-team/tasks-activity-log",     name: "Tasks Activity Log" },
];

export function serializeCatalog() {
  return ROUTE_CATALOG.map((r) => ({
    path: r.path,
    name: r.name,
    ...(r.params?.length       ? { params: r.params }           : {}),
    ...(r.tabs?.length         ? { tabs: r.tabs }               : {}),
    ...(r.query && Object.keys(r.query).length ? { query: r.query } : {}),
  }));
}
